// ===== Mock Adapter =====
// In-memory adapter for local development, demo, and testing.
// No external dependencies required. Data is lost on page refresh
// unless configured with persistence (Zustand persist middleware).
//
// NOTE: This adapter now persists all mutations to localStorage
// (key: "munshis-mock-db"), so admin edits survive page refreshes.
// Seed data below is the DEFAULT; once the user saves changes, the
// localStorage snapshot takes precedence.

const STORAGE_KEY = 'munshis-mock-db';

import {
  DataAdapter,
  AuthSession,
  AuthCredentials,
  RegisterInput,
  FamilyTreeRecord,
  CreateTreeInput,
  FamilyMemberRecord,
  CreateMemberInput,
  MemberRelationship,
  MemberSubmission,
  CreateSubmissionInput,
  UpdateSubmissionInput,
  AuthError,
  NotFoundError,
} from './types';

// ─── Mock Database ────────────────────────────────────────

interface MockDB {
  users: Record<string, { password: string; user: AuthSession['user'] }>;
  trees: Record<string, FamilyTreeRecord>;
  members: Record<string, FamilyMemberRecord>;
  relationships: Record<string, MemberRelationship>;
  submissions: Record<string, MemberSubmission>;
  currentSession: AuthSession | null;
}

const db: {
  users: Map<string, { password: string; user: AuthSession['user'] }>;
  trees: Map<string, FamilyTreeRecord>;
  members: Map<string, FamilyMemberRecord>;
  relationships: Map<string, MemberRelationship>;
  submissions: Map<string, MemberSubmission>;
  currentSession: AuthSession | null;
} = {
  users: new Map(),
  trees: new Map(),
  members: new Map(),
  relationships: new Map(),
  submissions: new Map(),
  currentSession: null,
};

// ─── Persistence ──────────────────────────────────────────

function saveDB(): void {
  try {
    const snapshot: MockDB = {
      users: Object.fromEntries(db.users),
      trees: Object.fromEntries(db.trees),
      members: Object.fromEntries(db.members),
      relationships: Object.fromEntries(db.relationships),
      submissions: Object.fromEntries(db.submissions),
      currentSession: db.currentSession,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (e) {
    console.warn('[mock-adapter] Failed to persist to localStorage:', e);
  }
}

function loadDB(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const snapshot = JSON.parse(raw) as MockDB;
    db.users = new Map(Object.entries(snapshot.users ?? {}));
    db.trees = new Map(Object.entries(snapshot.trees ?? {}));
    db.members = new Map(Object.entries(snapshot.members ?? {}));
    db.relationships = new Map(Object.entries(snapshot.relationships ?? {}));
    db.submissions = new Map(Object.entries(snapshot.submissions ?? {}));
    db.currentSession = snapshot.currentSession ?? null;
  } catch (e) {
    console.warn('[mock-adapter] Failed to load persisted data:', e);
  }
}

/** Reset the mock database back to the seed data (admin utility). */
export function resetMockDB(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('[mock-adapter] Failed to clear persisted data:', e);
  }
  location.reload();
}

/**
 * Export the current in-memory DB as ready-to-paste TypeScript seed code.
 * Admin utility: paste the output into mock.adapter.ts's seed arrays and
 * commit so the deployed static site serves the new data.
 */
export function exportMockDBAsSeedCode(): string {
  const trees = Array.from(db.trees.values());
  const members = Array.from(db.members.values());
  const relationships = Array.from(db.relationships.values());

  const treeLines = trees.map(t => `  {\n    id: '${t.id}',\n    name: '${t.name}',\n    description: '${t.description ?? ''}',\n    memberCount: ${t.memberCount},\n    generationCount: ${t.generationCount},\n    lastUpdated: '${t.lastUpdated}',\n    createdAt: '${t.createdAt}',\n  },`);

  const memberLines = members.map(m => {
    const parts = [
      `id: '${m.id}'`,
      `treeId: '${m.treeId}'`,
      `name: '${m.name}'`,
    ];
    if (m.nickname) parts.push(`nickname: '${m.nickname}'`);
    parts.push(`birthDate: '${m.birthDate}'`);
    if (m.deathDate) parts.push(`deathDate: '${m.deathDate}'`);
    if (m.birthPlace) parts.push(`birthPlace: '${m.birthPlace}'`);
    if (m.currentLocation) parts.push(`currentLocation: '${m.currentLocation}'`);
    if (m.profession) parts.push(`profession: '${m.profession}'`);
    if (m.education) parts.push(`education: '${m.education}'`);
    parts.push(`gender: '${m.gender}'`);
    if (m.photoUrl) parts.push(`photoUrl: '${m.photoUrl}'`);
    if (m.email) parts.push(`email: '${m.email}'`);
    if (m.phone) parts.push(`phone: '${m.phone}'`);
    parts.push(`isAlive: ${m.isAlive}`);
    parts.push(`generation: ${m.generation}`);
    parts.push(`maritalStatus: '${m.maritalStatus}'`);
    if (m.notes) parts.push(`notes: '${m.notes}'`);
    return `  {\n    ${parts.join(',\n    ')},\n  },`;
  });

  const relLines = relationships.map(r => `  { id: '${r.id}', treeId: '${r.treeId}', memberId: '${r.memberId}', relatedId: '${r.relatedId}', type: '${r.type}' },`);

  return `// ─── Seed Trees ─────────────────────────────────────────
const seedTrees: FamilyTreeRecord[] = [
${treeLines.join('\n')}
];

// ─── Seed Members ────────────────────────────────────────
const seedMembers: FamilyMemberRecord[] = [
${memberLines.join('\n')}
];

// ─── Seed Relationships ─────────────────────────────────
const seedRelationships: MemberRelationship[] = [
${relLines.join('\n')}
];
`;
}


// ─── Seed Demo User ───────────────────────────────────────

db.users.set('demo@familytree.app', {
  password: 'demo123',
  user: {
    id: 'user-demo-001',
    email: 'demo@familytree.app',
    name: 'Demo User',
    familyName: 'Munshi',
    avatar: 'https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: '2024-01-01T00:00:00Z',
  },
});

// ─── Seed Demo Tree ───────────────────────────────────────

db.trees.set('Munshi-family', {
  id: 'Munshi-family',
  name: 'The Munshi Family',
  description: 'Extended family tree of the Munshi Bari',
  memberCount: 11,
  generationCount: 3,
  lastUpdated: '2024-01-15T10:30:00Z',
  createdAt: '2023-06-15T08:00:00Z',
});

// ─── Seed Mock Members ────────────────────────────────────

const seedMembers: FamilyMemberRecord[] = [
  {
    id: '1',
    treeId: 'Munshi-family',
    name: 'Mohammad Tain Ullah Munshi',
    birthDate: '',
    birthPlace: 'Sonargaon',
    currentLocation: 'Madina, KSA',
    gender: 'male',
    isAlive: false,
    generation: 1,
    maritalStatus: 'married',
  },
  {
    id: '2',
    treeId: 'Munshi-family',
    name: 'Wife',
    birthDate: '',
    birthPlace: 'Not recorded',
    currentLocation: 'Unknown',
    profession: 'Homemaker',
    gender: 'female',
    isAlive: false,
    generation: 1,
    maritalStatus: 'married',
  },
  {
    id: '3',
    treeId: 'Munshi-family',
    name: 'Md. Wahid Ali Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 2,
    maritalStatus: 'married',
  },
  {
    id: '5',
    treeId: 'Munshi-family',
    name: 'Moulovi Ahmad Ullah Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 2,
    maritalStatus: 'married',
  },
  {
    id: 'member-1786071058765-8dn5qbk',
    treeId: 'Munshi-family',
    name: 'Md. Amjad Ali Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 2,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786071443651-mftiyn8',
    treeId: 'Munshi-family',
    name: 'Md. Daim Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 2,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786071481076-34iuwdj',
    treeId: 'Munshi-family',
    name: 'Md. Kamil Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 2,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786071601232-gm9nqux',
    treeId: 'Munshi-family',
    name: '1st Daughter',
    birthDate: '',
    gender: 'female',
    isAlive: false,
    generation: 2,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786073579081-9r7k208',
    treeId: 'Munshi-family',
    name: '2nd Daughter',
    birthDate: '',
    gender: 'female',
    isAlive: false,
    generation: 2,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786073622935-7y98pqs',
    treeId: 'Munshi-family',
    name: '3rd Daughter',
    birthDate: '',
    gender: 'female',
    isAlive: false,
    generation: 2,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786073660218-s9qua06',
    treeId: 'Munshi-family',
    name: '4th Daughter',
    birthDate: '',
    gender: 'female',
    isAlive: false,
    generation: 2,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786073687223-0ma1m03',
    treeId: 'Munshi-family',
    name: 'Ebad Ullah Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 3,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786073766347-v9n8392',
    treeId: 'Munshi-family',
    name: 'Hamid Ullah Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 3,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786073786049-j4z68oz',
    treeId: 'Munshi-family',
    name: 'Golam Rahman Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 3,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786073854916-fs5w3r3',
    treeId: 'Munshi-family',
    name: 'Md. Tobarok Ali Munshi',
    birthDate: '',
    gender: 'male',
    isAlive: false,
    generation: 4,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786073937117-a3x3mcn',
    treeId: 'Munshi-family',
    name: 'Md. Shujait Ali Munshi',
    birthDate: '',
    currentLocation: 'Chattogram, BD',
    gender: 'male',
    isAlive: false,
    generation: 5,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786074162333-69d8e8a',
    treeId: 'Munshi-family',
    name: 'Md. Anwarul Azim',
    birthDate: '',
    currentLocation: 'Chattogram, BD',
    gender: 'male',
    isAlive: false,
    generation: 6,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786074222325-bqikwx9',
    treeId: 'Munshi-family',
    name: 'Md. Anwar Hossain Munshi',
    birthDate: '2026-08-07',
    currentLocation: 'Dhaka, BD',
    gender: 'male',
    phone: '+8801848135954',
    isAlive: true,
    generation: 6,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786074400128-43h85g0',
    treeId: 'Munshi-family',
    name: 'Md. Jahangir Alam',
    birthDate: '2026-08-07',
    birthPlace: 'Chittagong, BD',
    currentLocation: 'Dhaka, BD',
    profession: 'Lawyer',
    gender: 'male',
    phone: '++801727296254',
    isAlive: true,
    generation: 7,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786074502469-tb63can',
    treeId: 'Munshi-family',
    name: 'Afia Khatun',
    birthDate: '',
    currentLocation: 'Noakhali, BD',
    gender: 'female',
    isAlive: false,
    generation: 5,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786074698158-ugfqph1',
    treeId: 'Munshi-family',
    name: 'Fitima Johura',
    birthDate: 'undefined',
    gender: 'female',
    isAlive: true,
    generation: undefined,
    maritalStatus: 'undefined',
  },
  {
    id: 'member-1786074757452-1wudsto',
    treeId: 'Munshi-family',
    name: 'Md. Alamgir Shujait',
    birthDate: '',
    birthPlace: 'Chittagong, BD',
    currentLocation: 'Dhaka, BD',
    profession: 'Businessman',
    gender: 'male',
    isAlive: true,
    generation: 7,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786074854488-y92zrvn',
    treeId: 'Munshi-family',
    name: 'Baharul Hayat',
    birthDate: '',
    currentLocation: 'Makkah, KSA',
    gender: 'male',
    phone: '+966508052452',
    isAlive: true,
    generation: 7,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786074860186-qdt8kzy',
    treeId: 'Munshi-family',
    name: 'Golam Rabbani Munshi',
    birthDate: '',
    currentLocation: 'Misato, JP',
    profession: 'Businessman',
    gender: 'male',
    phone: '+818077045185',
    isAlive: true,
    generation: 7,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786075361617-h0dlsun',
    treeId: 'Munshi-family',
    name: 'Dilara Azim',
    birthDate: '',
    birthPlace: 'Chittagong, BD',
    currentLocation: 'Dhaka, BD',
    profession: 'Homemaker',
    gender: 'female',
    isAlive: true,
    generation: 7,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786075422003-hkekli2',
    treeId: 'Munshi-family',
    name: 'Ismat Ara',
    birthDate: '',
    birthPlace: 'Chittagong, BD',
    currentLocation: 'Dhaka, BD',
    profession: 'Homemaker',
    gender: 'female',
    isAlive: true,
    generation: 7,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786075501472-vnjrwtl',
    treeId: 'Munshi-family',
    name: 'Abu Zubair',
    birthDate: '1987-07-11',
    birthPlace: 'Chittagong, BD',
    currentLocation: 'Dhaka, BD',
    profession: 'Software Engineer',
    gender: 'male',
    phone: '+8801339847467',
    isAlive: true,
    generation: 8,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786075623009-ikulkme',
    treeId: 'Munshi-family',
    name: 'Zakaria Arafin',
    birthDate: '2026-08-07',
    birthPlace: 'Dhaka, BD',
    currentLocation: 'Tokyo, JP',
    profession: 'Teacher',
    gender: 'male',
    isAlive: true,
    generation: 8,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786075704806-q69mnhn',
    treeId: 'Munshi-family',
    name: 'Roshnee Mahajabin',
    birthDate: '2000-09-18',
    birthPlace: 'Dhaka, BD',
    currentLocation: 'Dhaka, BD',
    profession: 'Student',
    gender: 'female',
    isAlive: true,
    generation: 8,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786078014545-ow5jp7u',
    treeId: 'Munshi-family',
    name: 'Hidayah Zubair',
    birthDate: '2024-01-31',
    birthPlace: 'Dhaka, BD',
    currentLocation: 'Dhaka, BD',
    gender: 'female',
    isAlive: true,
    generation: 9,
    maritalStatus: 'single',
  },
  {
    id: 'member-1786078133695-hfmyaor',
    treeId: 'Munshi-family',
    name: 'Aisha Gutierrez',
    birthDate: '2026-08-07',
    birthPlace: 'Santiago,CL',
    currentLocation: 'Dhaka, BD',
    profession: 'Homemaker',
    gender: 'female',
    isAlive: true,
    generation: 8,
    maritalStatus: 'single',
  },
];

seedMembers.forEach(m => db.members.set(m.id, m));

// ─── Seed Relationships ───────────────────────────────────

const seedRelationships: MemberRelationship[] = [
  { id: 'rel-1', treeId: 'Munshi-family', memberId: '1', relatedId: '2', type: 'spouse' },
  { id: 'rel-2', treeId: 'Munshi-family', memberId: '2', relatedId: '1', type: 'spouse' },
  { id: 'rel-3', treeId: 'Munshi-family', memberId: '1', relatedId: '3', type: 'parent' },
  { id: 'rel-4', treeId: 'Munshi-family', memberId: '2', relatedId: '3', type: 'parent' },
  { id: 'rel-5', treeId: 'Munshi-family', memberId: '1', relatedId: '5', type: 'parent' },
  { id: 'rel-6', treeId: 'Munshi-family', memberId: '2', relatedId: '5', type: 'parent' },
  { id: 'rel-1786071058867-spohzny', treeId: 'Munshi-family', memberId: '1', relatedId: 'member-1786071058765-8dn5qbk', type: 'parent' },
  { id: 'rel-1786071443752-gyiff3a', treeId: 'Munshi-family', memberId: '1', relatedId: 'member-1786071443651-mftiyn8', type: 'parent' },
  { id: 'rel-1786071481177-7pji5sc', treeId: 'Munshi-family', memberId: '1', relatedId: 'member-1786071481076-34iuwdj', type: 'parent' },
  { id: 'rel-1786071601333-ujke29z', treeId: 'Munshi-family', memberId: '1', relatedId: 'member-1786071601232-gm9nqux', type: 'parent' },
  { id: 'rel-1786073579183-ss3vou8', treeId: 'Munshi-family', memberId: '1', relatedId: 'member-1786073579081-9r7k208', type: 'parent' },
  { id: 'rel-1786073623036-bywd497', treeId: 'Munshi-family', memberId: '1', relatedId: 'member-1786073622935-7y98pqs', type: 'parent' },
  { id: 'rel-1786073660319-r1t1vbk', treeId: 'Munshi-family', memberId: '1', relatedId: 'member-1786073660218-s9qua06', type: 'parent' },
  { id: 'rel-1786073687324-5qb4s2m', treeId: 'Munshi-family', memberId: 'member-1786071058765-8dn5qbk', relatedId: 'member-1786073687223-0ma1m03', type: 'parent' },
  { id: 'rel-1786073766448-vvcneow', treeId: 'Munshi-family', memberId: 'member-1786071058765-8dn5qbk', relatedId: 'member-1786073766347-v9n8392', type: 'parent' },
  { id: 'rel-1786073786150-6pkofa6', treeId: 'Munshi-family', memberId: 'member-1786071058765-8dn5qbk', relatedId: 'member-1786073786049-j4z68oz', type: 'parent' },
  { id: 'rel-1786073855017-l4lh94w', treeId: 'Munshi-family', memberId: 'member-1786073687223-0ma1m03', relatedId: 'member-1786073854916-fs5w3r3', type: 'parent' },
  { id: 'rel-1786073937218-oz61a8z', treeId: 'Munshi-family', memberId: 'member-1786073854916-fs5w3r3', relatedId: 'member-1786073937117-a3x3mcn', type: 'parent' },
  { id: 'rel-1786074162434-5a1rd9m', treeId: 'Munshi-family', memberId: 'member-1786073937117-a3x3mcn', relatedId: 'member-1786074162333-69d8e8a', type: 'parent' },
  { id: 'rel-1786074222426-ykgrgc1', treeId: 'Munshi-family', memberId: 'member-1786073937117-a3x3mcn', relatedId: 'member-1786074222325-bqikwx9', type: 'parent' },
  { id: 'rel-1786074400229-2ej2sn1', treeId: 'Munshi-family', memberId: 'member-1786074162333-69d8e8a', relatedId: 'member-1786074400128-43h85g0', type: 'parent' },
  { id: 'rel-1786074502570-t74ppx0', treeId: 'Munshi-family', memberId: 'member-1786073937117-a3x3mcn', relatedId: 'member-1786074502469-tb63can', type: 'spouse' },
  { id: 'rel-1786074502671-wv4mj5p', treeId: 'Munshi-family', memberId: 'member-1786074502469-tb63can', relatedId: 'member-1786073937117-a3x3mcn', type: 'spouse' },
  { id: 'rel-1786074698259-9xdq1xl', treeId: 'Munshi-family', memberId: 'member-1786074162333-69d8e8a', relatedId: 'member-1786074698158-ugfqph1', type: 'spouse' },
  { id: 'rel-1786074698360-digkk2c', treeId: 'Munshi-family', memberId: 'member-1786074698158-ugfqph1', relatedId: 'member-1786074162333-69d8e8a', type: 'spouse' },
  { id: 'rel-1786074757553-5xjhp9e', treeId: 'Munshi-family', memberId: 'member-1786074162333-69d8e8a', relatedId: 'member-1786074757452-1wudsto', type: 'parent' },
  { id: 'rel-1786074854589-3hytiuy', treeId: 'Munshi-family', memberId: 'member-1786074162333-69d8e8a', relatedId: 'member-1786074854488-y92zrvn', type: 'parent' },
  { id: 'rel-1786074860287-lodmji7', treeId: 'Munshi-family', memberId: 'member-1786074162333-69d8e8a', relatedId: 'member-1786074860186-qdt8kzy', type: 'parent' },
  { id: 'rel-1786075361719-lss8n11', treeId: 'Munshi-family', memberId: 'member-1786074162333-69d8e8a', relatedId: 'member-1786075361617-h0dlsun', type: 'parent' },
  { id: 'rel-1786075422105-efgtj9l', treeId: 'Munshi-family', memberId: 'member-1786074162333-69d8e8a', relatedId: 'member-1786075422003-hkekli2', type: 'parent' },
  { id: 'rel-1786075501574-e6os7u1', treeId: 'Munshi-family', memberId: 'member-1786074400128-43h85g0', relatedId: 'member-1786075501472-vnjrwtl', type: 'parent' },
  { id: 'rel-1786075623110-s9wq9qy', treeId: 'Munshi-family', memberId: 'member-1786074400128-43h85g0', relatedId: 'member-1786075623009-ikulkme', type: 'parent' },
  { id: 'rel-1786075704908-lu32ln8', treeId: 'Munshi-family', memberId: 'member-1786074400128-43h85g0', relatedId: 'member-1786075704806-q69mnhn', type: 'parent' },
  { id: 'rel-1786078014646-0t3ftod', treeId: 'Munshi-family', memberId: 'member-1786075501472-vnjrwtl', relatedId: 'member-1786078014545-ow5jp7u', type: 'parent' },
  { id: 'rel-1786078133796-yhfgjt0', treeId: 'Munshi-family', memberId: 'member-1786075501472-vnjrwtl', relatedId: 'member-1786078133695-hfmyaor', type: 'spouse' },
  { id: 'rel-1786078133897-ahquejl', treeId: 'Munshi-family', memberId: 'member-1786078133695-hfmyaor', relatedId: 'member-1786075501472-vnjrwtl', type: 'spouse' },
];

seedRelationships.forEach(r => db.relationships.set(r.id, r));

// Restore any persisted (admin-edited) data over the seed defaults.
loadDB();

// ─── Adapter Implementation ───────────────────────────────

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export class MockAdapter implements DataAdapter {
  readonly name = 'mock';
  readonly version = '1.0.0';
  readonly description = 'In-memory mock adapter for local development & testing';

  // ── Auth ────────────────────────────────────────────────

  async login({ email, password }: AuthCredentials): Promise<AuthSession> {
    await delay(300);
    const record = db.users.get(email.toLowerCase());
    if (!record || record.password !== password) {
      throw new AuthError('Invalid email or password', 'INVALID_CREDENTIALS');
    }
    const session: AuthSession = { user: record.user, token: `mock-token-${uid()}` };
    db.currentSession = session;
    saveDB();
    return session;
  }

  async register(input: RegisterInput): Promise<AuthSession> {
    await delay(300);
    const email = input.email.toLowerCase();
    if (db.users.has(email)) {
      throw new AuthError('Email is already registered', 'EMAIL_EXISTS');
    }
    const user = {
      id: `user-${uid()}`,
      email,
      name: input.name,
      familyName: input.familyName,
      createdAt: new Date().toISOString(),
    };
    db.users.set(email, { password: input.password, user });
    const session: AuthSession = { user, token: `mock-token-${uid()}` };
    db.currentSession = session;
    saveDB();
    return session;
  }

  async logout(): Promise<void> {
    db.currentSession = null;
    saveDB();
  }

  async getSession(): Promise<AuthSession | null> {
    return db.currentSession;
  }

  async refreshSession(): Promise<AuthSession | null> {
    return db.currentSession;
  }

  // ── Family Trees ────────────────────────────────────────

  async listTrees(): Promise<FamilyTreeRecord[]> {
    await delay(100);
    return Array.from(db.trees.values());
  }

  async getTree(id: string): Promise<FamilyTreeRecord | null> {
    await delay(50);
    return db.trees.get(id) ?? null;
  }

  async createTree(input: CreateTreeInput): Promise<FamilyTreeRecord> {
    await delay(200);
    const tree: FamilyTreeRecord = {
      id: `tree-${uid()}`,
      name: input.name,
      description: input.description,
      memberCount: 0,
      generationCount: 0,
      lastUpdated: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };
    db.trees.set(tree.id, tree);
    saveDB();
    return tree;
  }

  async updateTree(id: string, data: Partial<FamilyTreeRecord>): Promise<FamilyTreeRecord> {
    await delay(100);
    const existing = db.trees.get(id);
    if (!existing) throw new NotFoundError(`Tree "${id}" not found`);
    const updated = { ...existing, ...data, lastUpdated: new Date().toISOString() };
    db.trees.set(id, updated);
    saveDB();
    return updated;
  }

  async deleteTree(id: string): Promise<void> {
    await delay(100);
    if (!db.trees.has(id)) throw new NotFoundError(`Tree "${id}" not found`);
    db.trees.delete(id);
    // Cascade: delete related members and relationships
    for (const [mid, m] of db.members) {
      if (m.treeId === id) db.members.delete(mid);
    }
    for (const [rid, r] of db.relationships) {
      if (r.treeId === id) db.relationships.delete(rid);
    }
    saveDB();
  }

  // ── Family Members ──────────────────────────────────────

  async listMembers(treeId: string): Promise<FamilyMemberRecord[]> {
    await delay(100);
    return Array.from(db.members.values()).filter(m => m.treeId === treeId);
  }

  async getMember(id: string): Promise<FamilyMemberRecord | null> {
    await delay(50);
    return db.members.get(id) ?? null;
  }

  async createMember(treeId: string, input: CreateMemberInput): Promise<FamilyMemberRecord> {
    await delay(200);
    if (!db.trees.has(treeId)) throw new NotFoundError(`Tree "${treeId}" not found`);
    const member: FamilyMemberRecord = {
      id: `member-${uid()}`,
      treeId,
      name: input.name,
      nickname: input.nickname,
      gender: input.gender,
      birthDate: input.birthDate,
      birthPlace: input.birthPlace,
      isAlive: input.isAlive ?? true,
      deathDate: input.deathDate,
      generation: input.generation ?? 1,
      maritalStatus: input.maritalStatus ?? 'single',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    db.members.set(member.id, member);

    // Update tree member count
    const tree = db.trees.get(treeId)!;
    const treeMembers = Array.from(db.members.values()).filter(m => m.treeId === treeId);
    db.trees.set(treeId, { ...tree, memberCount: treeMembers.length, lastUpdated: new Date().toISOString() });

    saveDB();
    return member;
  }

  async updateMember(id: string, data: Partial<FamilyMemberRecord>): Promise<FamilyMemberRecord> {
    await delay(100);
    const existing = db.members.get(id);
    if (!existing) throw new NotFoundError(`Member "${id}" not found`);
    const updated = { ...existing, ...data, updatedAt: new Date().toISOString() };
    db.members.set(id, updated);
    saveDB();
    return updated;
  }

  async deleteMember(id: string): Promise<void> {
    await delay(100);
    const member = db.members.get(id);
    if (!member) throw new NotFoundError(`Member "${id}" not found`);
    db.members.delete(id);
    // Clean up orphaned relationships
    for (const [rid, r] of db.relationships) {
      if (r.memberId === id || r.relatedId === id) db.relationships.delete(rid);
    }
    // Update tree member count
    const tree = db.trees.get(member.treeId);
    if (tree) {
      const treeMembers = Array.from(db.members.values()).filter(m => m.treeId === member.treeId);
      db.trees.set(member.treeId, { ...tree, memberCount: treeMembers.length, lastUpdated: new Date().toISOString() });
    }
    saveDB();
  }

  // ── Relationships ───────────────────────────────────────

  async listRelationships(treeId: string): Promise<MemberRelationship[]> {
    await delay(50);
    return Array.from(db.relationships.values()).filter(r => r.treeId === treeId);
  }

  async createRelationship(
    treeId: string,
    memberId: string,
    relatedId: string,
    type: MemberRelationship['type'],
  ): Promise<MemberRelationship> {
    await delay(100);
    const rel: MemberRelationship = {
      id: `rel-${uid()}`,
      treeId,
      memberId,
      relatedId,
      type,
    };
    db.relationships.set(rel.id, rel);
    saveDB();
    return rel;
  }

  async deleteRelationship(id: string): Promise<void> {
    await delay(50);
    db.relationships.delete(id);
    saveDB();
  }

  // ── Member Submissions ─────────────────────────────────

  async createSubmission(input: CreateSubmissionInput): Promise<MemberSubmission> {
    await delay(200);
    if (!db.trees.has(input.treeId)) throw new NotFoundError(`Tree "${input.treeId}" not found`);
    const submission: MemberSubmission = {
      id: `sub-${uid()}`,
      treeId: input.treeId,
      name: input.name.trim(),
      nickname: input.nickname,
      birthDate: input.birthDate,
      deathDate: input.deathDate,
      birthPlace: input.birthPlace,
      currentLocation: input.currentLocation,
      profession: input.profession,
      education: input.education,
      gender: input.gender ?? 'male',
      maritalStatus: input.maritalStatus ?? 'single',
      isAlive: input.isAlive ?? true,
      notes: input.notes,
      relationship: input.relationship ?? null,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    db.submissions.set(submission.id, submission);
    saveDB();
    return submission;
  }

  async listSubmissions(treeId?: string): Promise<MemberSubmission[]> {
    await delay(100);
    const all = Array.from(db.submissions.values());
    return treeId ? all.filter(s => s.treeId === treeId) : all;
  }

  async getSubmission(id: string): Promise<MemberSubmission | null> {
    await delay(50);
    return db.submissions.get(id) ?? null;
  }

  async updateSubmission(id: string, data: UpdateSubmissionInput): Promise<MemberSubmission> {
    await delay(100);
    const existing = db.submissions.get(id);
    if (!existing) throw new NotFoundError(`Submission "${id}" not found`);
    if (existing.status !== 'pending') {
      throw new AuthError('Only pending submissions can be edited', 'CONFLICT', 409);
    }
    const updated: MemberSubmission = {
      ...existing,
      ...data,
      updatedAt: new Date().toISOString(),
    };
    db.submissions.set(id, updated);
    saveDB();
    return updated;
  }

  async approveSubmission(id: string): Promise<{ member: FamilyMemberRecord }> {
    await delay(200);
    const existing = db.submissions.get(id);
    if (!existing) throw new NotFoundError(`Submission "${id}" not found`);
    if (existing.status !== 'pending') {
      throw new AuthError('Submission has already been reviewed', 'CONFLICT', 409);
    }

    // Create the member
    const member: FamilyMemberRecord = {
      id: `member-${uid()}`,
      treeId: existing.treeId,
      name: existing.name,
      nickname: existing.nickname,
      birthDate: existing.birthDate ?? '',
      deathDate: existing.deathDate,
      birthPlace: existing.birthPlace,
      currentLocation: existing.currentLocation,
      profession: existing.profession,
      education: existing.education,
      gender: existing.gender,
      isAlive: existing.isAlive,
      generation: 1,
      maritalStatus: existing.maritalStatus,
      notes: existing.notes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    db.members.set(member.id, member);

    // Create relationship if requested
    if (existing.relationship?.relatedMemberId) {
      const relatedId = existing.relationship.relatedMemberId;
      const relatedExists = db.members.has(relatedId);
      if (!relatedExists) throw new AuthError('Related member not found in this tree', 'INVALID_RELATIONSHIP', 400);
      const type = existing.relationship.type;
      if (type === 'parent') {
        db.relationships.set(`rel-${uid()}`, { id: `rel-${uid()}`, treeId: existing.treeId, memberId: relatedId, relatedId: member.id, type: 'parent' });
      } else if (type === 'child') {
        db.relationships.set(`rel-${uid()}`, { id: `rel-${uid()}`, treeId: existing.treeId, memberId: member.id, relatedId, type: 'parent' });
      } else if (type === 'spouse') {
        db.relationships.set(`rel-${uid()}`, { id: `rel-${uid()}`, treeId: existing.treeId, memberId: member.id, relatedId, type: 'spouse' });
        db.relationships.set(`rel-${uid()}`, { id: `rel-${uid()}`, treeId: existing.treeId, memberId: relatedId, relatedId: member.id, type: 'spouse' });
      } else if (type === 'sibling') {
        db.relationships.set(`rel-${uid()}`, { id: `rel-${uid()}`, treeId: existing.treeId, memberId: member.id, relatedId, type: 'sibling' });
      }
    }

    // Update tree counts
    const tree = db.trees.get(existing.treeId);
    if (tree) {
      const treeMembers = Array.from(db.members.values()).filter(m => m.treeId === existing.treeId);
      const generations = new Set(treeMembers.map(m => m.generation));
      db.trees.set(existing.treeId, {
        ...tree,
        memberCount: treeMembers.length,
        generationCount: generations.size,
        lastUpdated: new Date().toISOString(),
      });
    }

    // Mark approved
    const updated: MemberSubmission = {
      ...existing,
      status: 'approved',
      reviewedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    db.submissions.set(id, updated);
    saveDB();
    return { member };
  }

  async rejectSubmission(id: string): Promise<MemberSubmission> {
    await delay(100);
    const existing = db.submissions.get(id);
    if (!existing) throw new NotFoundError(`Submission "${id}" not found`);
    if (existing.status !== 'pending') {
      throw new AuthError('Submission has already been reviewed', 'CONFLICT', 409);
    }
    const updated: MemberSubmission = {
      ...existing,
      status: 'rejected',
      reviewedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    db.submissions.set(id, updated);
    saveDB();
    return updated;
  }
}
